import { API_URLS } from "../Constants/api.constants";
import api from "./api";
import { errorHandling } from "./error.handling";

// Contatos são um sub-recurso do serviço de clientes (mesmo NC8_BASE/Customer
// usado por client.service.js) — endpoints reais em /api/v3/contacts/...
const basePath = `${API_URLS.CUSTOMERS}/contacts`;

// ───────────────────────── Consultas (GET) ─────────────────────────

async function fetchPhoneTypes() {
  try {
    let response = await api.get(`${basePath}/phone-types`);
    let treated = errorHandling.handleResponse(response, "contactsService.fetchPhoneTypes");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.fetchPhoneTypes");
    return null;
  }
}

async function fetchRelationships() {
  try {
    let response = await api.get(`${basePath}/relationships`);
    let treated = errorHandling.handleResponse(response, "contactsService.fetchRelationships");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.fetchRelationships");
    return null;
  }
}

async function fetchByClient(clientId) {
  try {
    let response = await api.get(`${basePath}/clients/${clientId}`);
    let treated = errorHandling.handleResponse(response, "contactsService.fetchByClient");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    if (error?.response?.status === 404) {
      return [];
    }
    errorHandling.cathError(error, "contactsService.fetchByClient");
    return null;
  }
}

async function fetchEmails(contactId) {
  try {
    let response = await api.get(`${basePath}/${contactId}/emails`);
    let treated = errorHandling.handleResponse(response, "contactsService.fetchEmails");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.fetchEmails");
    return null;
  }
}

async function fetchPhones(contactId) {
  try {
    let response = await api.get(`${basePath}/${contactId}/phones`);
    let treated = errorHandling.handleResponse(response, "contactsService.fetchPhones");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.fetchPhones");
    return null;
  }
}



async function createContact(payload) {
  try {
    const requestPayload = {
      name: payload?.name ?? payload?.Name ?? "",
      doc: (payload?.doc ?? payload?.Doc ?? "").replace(/\D/g, ""),
      relationshipId: payload?.relationshipId ?? payload?.RelationshipId,
    };

    let response = await api.post(basePath, JSON.stringify(requestPayload));
    let treated = errorHandling.handleResponse(response, "contactsService.createContact");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.createContact");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

async function createEmail(payload) {
  try {
    const requestPayload = {
      contactId: payload?.contactId ?? payload?.ContactId,
      email: payload?.email ?? payload?.Email ?? "",
    };

    let response = await api.post(`${basePath}/emails`, JSON.stringify(requestPayload));
    let treated = errorHandling.handleResponse(response, "contactsService.createEmail");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.createEmail");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

async function createPhone(payload) {
  try {
    const requestPayload = {
      contactId: payload?.contactId ?? payload?.ContactId,
      phone: payload?.phone ?? payload?.Phone ?? payload?.number ?? payload?.Number ?? "",
      extension: payload?.extension ?? payload?.Extension ?? "",
      phoneTypeId: payload?.phoneTypeId ?? payload?.PhoneTypeId,
    };

    let response = await api.post(`${basePath}/phones`, JSON.stringify(requestPayload));
    let treated = errorHandling.handleResponse(response, "contactsService.createPhone");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.createPhone");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

async function updateContact(contactId, payload) {
  try {
    const requestPayload = {
      contactId: payload?.contactId ?? payload?.ContactId ?? contactId,
      name: payload?.name ?? payload?.Name ?? "",
      doc: (payload?.doc ?? payload?.Doc ?? "").replace(/\D/g, ""),
      relationshipId: payload?.relationshipId ?? payload?.RelationshipId,
    };

    let response = await api.put(`${basePath}/${contactId}`, JSON.stringify(requestPayload));
    let treated = errorHandling.handleResponse(response, "contactsService.updateContact");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.updateContact");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

async function deleteContact(clientId, contactId) {
  try {
    let response = await api.delete(`${basePath}/clients/${clientId}/contacts/${contactId}`);
    // 204 (No Content) é sucesso pra delete — handleResponse trata isso como
    // "sem dado" e devolve null, indistinguível de uma falha. Sem esse
    // desvio, a tela mostrava toast de erro num delete que na verdade
    // funcionou.
    if (response.status === 204) {
      return { success: true };
    }
    let treated = errorHandling.handleResponse(response, "contactsService.deleteContact");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.deleteContact");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

// ───────────────────────── Edição (PUT) ─────────────────────────
// Mesma ressalva do POST: esquema do corpo é uma suposição razoável a
// partir dos campos que a tela usa, não veio documentado.

async function updateEmail(contactEmailId, payload) {
  try {
    const requestPayload = {
      contactId: payload?.contactId ?? payload?.ContactId,
      email: payload?.email ?? payload?.Email ?? "",
    };

    let response = await api.put(`${basePath}/emails/${contactEmailId}`, JSON.stringify(requestPayload));
    let treated = errorHandling.handleResponse(response, "contactsService.updateEmail");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.updateEmail");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

async function updatePhone(contactPhoneId, payload) {
  try {
      const requestPayload = {
      contactId: payload?.contactId ?? payload?.ContactId,
      phone: payload?.phone ?? payload?.Phone ?? payload?.number ?? payload?.Number ?? "",
      extension: payload?.extension ?? payload?.Extension ?? "",
      phoneTypeId: payload?.phoneTypeId ?? payload?.PhoneTypeId,
    };

    let response = await api.put(`${basePath}/phones/${contactPhoneId}`, JSON.stringify(requestPayload));
    let treated = errorHandling.handleResponse(response, "contactsService.updatePhone");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.updatePhone");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

// ───────────────────────── Exclusão (DELETE) ─────────────────────────

async function deleteEmail(contactEmailId) {
  try {
    let response = await api.delete(`${basePath}/emails/${contactEmailId}`);
    // Mesmo caso do deleteContact: 204 é sucesso, mas handleResponse devolve
    // null pra ele — sem esse desvio, viraria toast de erro num delete que
    // deu certo.
    if (response.status === 204) {
      return { success: true };
    }
    let treated = errorHandling.handleResponse(response, "contactsService.deleteEmail");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.deleteEmail");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

async function deletePhone(contactPhoneId) {
  try {
    let response = await api.delete(`${basePath}/phones/${contactPhoneId}`);
    // Mesmo caso do deleteContact/deleteEmail: 204 é sucesso, mas
    // handleResponse devolve null pra ele.
    if (response.status === 204) {
      return { success: true };
    }
    let treated = errorHandling.handleResponse(response, "contactsService.deletePhone");
    if (treated) {
      return treated;
    }
    return null;
  } catch (error) {
    errorHandling.cathError(error, "contactsService.deletePhone");
    return {
      error: true,
      status: error?.response?.status,
      message: error?.response?.data?.message,
      friendlyMessage: error?.response?.data?.friendlyMessage,
    };
  }
}

export const contactsService = {
  // Consultas
  fetchPhoneTypes,
  fetchRelationships,
  fetchByClient,
  fetchEmails,
  fetchPhones,
  // Inclusão
  createContact,
  createEmail,
  createPhone,
 // Edição
  updateContact,
  updateEmail,
  updatePhone,
  // Exclusão
  deleteContact,
  deleteEmail,
  deletePhone,
};
